import { ref, computed, onMounted, onUnmounted } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { Popover } from 'bootstrap';

/**
 * useCambiarPlaza
 * Composable that manages plaza/SVG rendering state and interactions
 * for the CambiarPlaza seat/plaza selection component.
 *
 * @param {Object} options
 * @param {Ref<Number>} options.tipo_svg - SVG type (1 = normal, else leaflet)
 * @param {Ref<Object|null>} options.zona - Selected zone
 * @param {Ref<Object|null>} options.plaza_abonado - Current subscriber's plaza
 * @param {Function} options.onCambiarPlaza - Emit callback for plaza change
 * @returns {Object} - Public API for the component template
 */
export function useCambiarPlaza({ tipo_svg, zona, plaza_abonado, onCambiarPlaza }) {
    // ==================== STATE ====================
    const svg_loaded = ref('');
    const loading = ref(true);
    const svgpanzoom = ref(null);
    const svg = ref(null);
    const svgRendered = ref(null);
    const isPanning = ref(false);
    const plazas = ref(null);
    const popoverInstances = ref([]);
    const loadSvgTimer = ref(null);
    const circleListeners = ref([]);

    // Color constants
    const originalColor = '#0CBA31';
    const seleccionadaColor = '#457ad1';
    const bloqueadaColor = '#BF95C4';
    const enSesionColor = 'yellow';
    const vendidaAbonosColor = 'black';
    const vendidaEventosColor = 'red';
    const bloqueadaEventosColor = 'purple';
    const enSesionEventosColor = 'orange';
    const abonadoNoRenovadoColor = '#B05D04';

    // ==================== COMPUTED ===================
    const lista_id = computed(() => store.getters.getListaId);
    const recinto_id = computed(() => store.getters.getRecinto?.id);

    // ==================== METHODS ====================
    async function getButacas() {
        loading.value = true;
        let peticion = 'back.lista.settings.recinto.butacas';

        const params = {
            filters: {},
            lista_id: lista_id.value,
            recinto_id: recinto_id.value
        };

        if (zona.value) {
            params.region_id = zona.value.id;
            peticion = 'back.abonos.listado-butacas-region';
        }

        try {
            const res = await store.dispatch(config.getVuexAlias(peticion), params);
            plazas.value = res.data.data;
            await getSvg();
        } catch (err) {
            console.error('Error in getButacas:', err);
        } finally {
            loading.value = false;
        }
    }

    async function getSvg() {
        loading.value = true;
        let peticion = 'front.recinto.svg';

        const params = {
            recinto_id: recinto_id.value
        };

        if (zona.value) {
            params.region_id = zona.value.id;
            peticion = 'recinto.zona-svg';
        }

        try {
            const { data: { svg: svgData } } = await store.dispatch(config.getVuexAlias(peticion), params);
            if (tipo_svg.value === 1) {
                loadSVG(svgData);
            } else {
                renderLeafletSvg(svgData);
            }
        } catch (err) {
            console.error('Error in getSvg:', err);
        } finally {
            loading.value = false;
        }
    }

    function loadSVG(svgData) {
        svg_loaded.value = svgData;

        if (loadSvgTimer.value) clearTimeout(loadSvgTimer.value);
        loadSvgTimer.value = setTimeout(() => {
            const plazasElements = document.getElementsByClassName('recinto-zona');
            for (const element of Array.from(plazasElements)) {
                if (element.dataset !== undefined) {
                    const plaza_id = element.dataset.region;
                    const plaza = plazas.value.find(p => String(p.id) === plaza_id);
                    if (plaza) {
                        if (!plaza.esta_disponible) {
                            element.classList.add('plaza_ocupada');
                        }
                        if (plaza.esta_bloqueada) {
                            element.classList.remove('plaza_ocupada');
                            element.classList.add('plaza_bloqueada');
                        }
                    }
                }
            }

            // Create popovers for each plaza
            for (const plaza of plazas.value) {
                const element = document.getElementById('popover-' + plaza.id);
                if (element) {
                    let content = '';
                    if (plaza.concepto) {
                        content += '<div><strong>Concepto: </strong>' + plaza.concepto + '</div>';
                    }
                    if (content) {
                        const popover = new Popover(element, {
                            content: content,
                            trigger: 'hover',
                            html: true
                        });
                        popoverInstances.value.push(popover);
                    }
                }
            }
        }, 1000);

        svgRendered.value = true;
    }

    function renderLeafletSvg(svgData) {
        const plazasFiltered = (plazas.value || []).filter(plaza => plaza.svg_id);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = svgData;

        const svgElement = tempDiv.firstElementChild;
        const butacas = svgElement.querySelectorAll('[id^="butaca"]');

        butacas.forEach(circle => {
            circle.setAttribute('seleccionado', 'false');
            circle.setAttribute('opacity', '0.8');
            const svg_id = getButacaId(circle);
            const plaza = plazasFiltered.find(plaza => plaza.svg_id === svg_id);
            if (plaza) {
                const title = getButacaName(plaza);

                if (plaza.libre && !plaza.ocupada?.bloqueada) {
                    drawPopover(circle, plaza, title);
                    circle.style.cursor = 'pointer';
                    circle.style.transition = 'fill 0.2s';
                    circle.setAttribute('fill', originalColor);

                    const libreClickHandler = () => {
                        const isSeleccionado = JSON.parse(circle.getAttribute('seleccionado'));

                        setTimeout(() => {
                            if (!isPanning.value) {
                                if (!isSeleccionado) {
                                    circle.setAttribute('fill', seleccionadaColor);
                                    circle.setAttribute('seleccionado', 'true');
                                } else {
                                    circle.setAttribute('fill', originalColor);
                                    circle.setAttribute('seleccionado', 'false');
                                }
                                clickZona(plaza);
                            }
                        }, 150);
                    };
                    circle.addEventListener('click', libreClickHandler);
                    circleListeners.value.push({ circle, handler: libreClickHandler });
                } else if (plaza.ocupada?.bloqueada) {
                    drawPopover(circle, plaza, title);
                    const color = plaza.etiqueta_bloqueo?.color || bloqueadaColor;
                    circle.setAttribute('fill', color);
                    const bloqueadaClickHandler = () => {
                        const isSeleccionado = JSON.parse(circle.getAttribute('seleccionado'));

                        setTimeout(() => {
                            if (!isPanning.value) {
                                if (!isSeleccionado) {
                                    circle.setAttribute('fill', seleccionadaColor);
                                    circle.setAttribute('seleccionado', 'true');
                                } else {
                                    circle.setAttribute('fill', color);
                                    circle.setAttribute('seleccionado', 'false');
                                }
                                clickZona(plaza);
                            }
                        }, 150);
                    };
                    circle.addEventListener('click', bloqueadaClickHandler);
                    circleListeners.value.push({ circle, handler: bloqueadaClickHandler });
                } else if (plaza.ocupada?.sesion) {
                    circle.setAttribute('ocupada', 'true');
                    circle.style.cursor = 'not-allowed';
                    circle.setAttribute('fill', enSesionColor);
                    drawPopover(circle, plaza, title);
                } else if (plaza.ocupada?.venta) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', vendidaAbonosColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                } else if (plaza.ocupada?.evento_bloqueo) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', bloqueadaEventosColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                } else if (plaza.ocupada?.evento_venta) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', vendidaEventosColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                } else if (plaza.ocupada?.evento_sesion) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', enSesionEventosColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                } else if (plaza.ocupada?.info_ocupada?.numero_abonado && plaza.ocupada.renovado) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', vendidaEventosColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                } else if (plaza.info_ocupada?.numero_abonado && !plaza.ocupada?.renovado) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', abonadoNoRenovadoColor);
                    circle.style.cursor = 'not-allowed';
                    drawPopover(circle, plaza, title);
                }

                if (plaza.id === plaza_abonado.value?.id) {
                    circle.setAttribute('ocupada', 'true');
                    circle.setAttribute('fill', '#fdf749');
                    circle.style.cursor = 'not-allowed';
                    const existingListener = circleListeners.value.find(l => l.circle === circle);
                    if (existingListener) {
                        circle.removeEventListener('click', existingListener.handler);
                        circleListeners.value = circleListeners.value.filter(l => l.circle !== circle);
                    }
                }
            }
        });

        svgRendered.value = tempDiv.firstElementChild;
    }

    function drawPopover(circle, plaza, title = null, content = '') {
        let state = '';
        if (plaza.ocupada) {
            if (plaza.ocupada.bloqueada) {
                state = 'bloqueada';
                content += '<div><strong>Bloqueada</strong></div>';
                if (plaza.etiqueta_bloqueo?.name) {
                    content += '<div><strong>' + plaza.etiqueta_bloqueo.name + '</strong></div>';
                }
                if (plaza.info_ocupada?.concepto) {
                    content += '<div><strong>Concepto: </strong>' + plaza.info_ocupada.concepto + '</div>';
                }
            } else if (plaza.ocupada.venta) {
                state = 'venta';
                content += '<div><strong>Vendida</strong></div>';
            } else if (plaza.ocupada.sesion) {
                content += '<div><strong>En sesion</strong></div>';
            } else if (plaza.ocupada.evento_venta) {
                content += '<div><strong>Vendida en evento</strong></div>';
            } else if (plaza.ocupada.evento_bloqueo) {
                content += '<div><strong>Bloqueada en evento</strong></div>';
            } else if (plaza.ocupada.evento_sesion) {
                content += '<div><strong>En sesion en evento</strong></div>';
            }

            // Info extra común para vendida/bloqueada
            if (state === 'venta' || state === 'bloqueada') {
                const nombre = [plaza.info_ocupada?.nombre, plaza.info_ocupada?.apellido].filter(Boolean).join(' ');
                if (nombre) content += '<div><strong>' + nombre + '</strong></div>';
                if (plaza.info_ocupada?.numero_abonado) {
                    content += '<div><strong>Nº Abonado: ' + plaza.info_ocupada.numero_abonado + '</strong></div>';
                }
            }
            if (plaza.info_ocupada?.numero_abonado && !plaza.ocupada.renovado) {
                content += '<div><strong>Abonado no renovado</strong></div>';
            }
            if (plaza.info_ocupada?.qr_abonado) {
                content += '<div><strong>Codigo QR: ' + plaza.info_ocupada.qr_abonado + '</strong></div>';
            }
            if (plaza.info_ocupada?.id_abonado) {
                circle.style.cursor = 'pointer';
                const abonadoClickHandler = () => {
                    const url = '/admin/abonados/' + lista_id.value + '/' + plaza.info_ocupada.id_abonado;
                    window.open(url, '_blank');
                };
                circle.addEventListener('click', abonadoClickHandler);
                circleListeners.value.push({ circle, handler: abonadoClickHandler });
            }
        }

        if (!content && plaza.libre) {
            content = '<div><strong>Plaza libre</strong></div>';
        }

        const popoverOptions = {
            title: title,
            content: content,
            trigger: 'hover',
            html: true,
            container: 'body'
        };

        const popover = new Popover(circle, popoverOptions);
        popoverInstances.value.push(popover);
    }

    function getButacaName(butaca) {
        const nButaca = butaca.butaca;
        const nFila = butaca.fila;
        const sector = butaca.sector_name;
        const visibilidad_reducida = butaca.visibilidad_reducida;
        const porcentaje_visibilidad = butaca.porcentaje_visibilidad;
        let name = '';

        if (sector) {
            name += 'Sector: ' + sector + ' - ';
        }

        if (!nButaca && !nFila) {
            name += 'Plaza: ' + butaca.numero;
        } else {
            name += 'Fila: ' + nFila + ' - Butaca: ' + nButaca;
        }

        if (visibilidad_reducida) {
            name += '<span class="text-danger"> Visibilidad Reducida</span>';
        }

        if (porcentaje_visibilidad) {
            name += '<span class="text-danger">: ' + porcentaje_visibilidad + ' %</span>';
        }

        return name;
    }

    function getButacaId(circle) {
        const id = circle.id;
        const idParts = id.split('-');

        return idParts[1];
    }

    function clickZona(plazaSeleccionada) {
        // Eliminar cualquier popover visible del DOM
        disposePopovers();
        document.querySelectorAll('.popover').forEach(el => el.remove());
        const plazaEncontrada = plazas.value.find(plaza => plaza.id === plazaSeleccionada.id);

        if (plazaEncontrada) {
            plazaEncontrada.seleccionada = !plazaEncontrada.seleccionada;

            if (onCambiarPlaza) {
                onCambiarPlaza(null, null, 0, plazaEncontrada.id);
            }
        }
    }

    function registerSvgPanZoom(instance) {
        svgpanzoom.value = instance;
    }

    function disposePopovers() {
        for (const popover of popoverInstances.value) {
            try {
                popover.dispose();
            } catch (e) {
                // Ignore disposal errors
            }
        }
        popoverInstances.value = [];
    }

    function disposeCircleListeners() {
        for (const { circle, handler } of circleListeners.value) {
            circle.removeEventListener('click', handler);
        }
        circleListeners.value = [];
    }

    // ==================== LIFECYCLE ====================
    onMounted(() => {
        getButacas();
    });

    onUnmounted(() => {
        disposePopovers();
        disposeCircleListeners();
        if (loadSvgTimer.value) {
            clearTimeout(loadSvgTimer.value);
        }
    });

    // ==================== PUBLIC API ====================
    return {
        // State
        svg_loaded,
        loading,
        svgpanzoom,
        svgRendered,
        isPanning,
        plazas,

        // Computed
        lista_id,
        recinto_id,

        // Color constants
        originalColor,
        seleccionadaColor,
        bloqueadaColor,
        enSesionColor,
        vendidaAbonosColor,
        vendidaEventosColor,
        bloqueadaEventosColor,
        enSesionEventosColor,
        abonadoNoRenovadoColor,

        // Methods
        getButacas,
        getSvg,
        loadSVG,
        renderLeafletSvg,
        drawPopover,
        getButacaName,
        getButacaId,
        clickZona,
        registerSvgPanZoom,
        disposePopovers,
        disposeCircleListeners
    };
}
